import { useHandler } from "@redotech/react-util/hook";
import { CustomerTagRecord } from "@redotech/redo-common-model-tags-common/customer-tag-record";
import {
  CustomerTagOperation,
  ManageCustomerTagsStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  LabeledInput,
  LabelOrientation,
  LabelPosition,
  LabelSize,
} from "@redotech/redo-web/labeled-input";
import { memo, useMemo } from "react";
import { FrontendStepType, PartialWithRequiredType } from "../steps";
import { CustomerTagsMultiSelect } from "./common/customer-tags-multi-select";

const CustomerTagManager = memo(function CustomerTagManager({
  step,
  setStep,
  showValidationErrors,
}: {
  step: PartialWithRequiredType<ManageCustomerTagsStep>;
  setStep: (step: PartialWithRequiredType<ManageCustomerTagsStep>) => void;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();
  const operationOptions = useMemo(
    () => [
      {
        id: "add",
        text: t("Add tags"),
        type: "text" as const,
        value: CustomerTagOperation.ADD,
      },
      {
        id: "remove",
        text: t("Remove tags"),
        type: "text" as const,
        value: CustomerTagOperation.REMOVE,
      },
    ],
    [t],
  );

  const selectedOperation = useMemo(
    () => operationOptions.find((op) => op.value === step.operation),
    [operationOptions, step.operation],
  );

  const handleOperationSelect = useHandler((option: any) => {
    // Clear tag selection when changing operation
    setStep({ ...step, operation: option.value, tagIds: [] });
  });

  const setSelectedTags = useHandler((tags: CustomerTagRecord[]) => {
    setStep({ ...step, tagIds: tags.map((tag) => tag._id) });
  });

  return (
    <Flex dir="column" gap="md">
      <LabeledInput
        label={t("Operation")}
        orientation={LabelOrientation.VERTICAL}
        position={LabelPosition.ABOVE}
        size={LabelSize.EXTRA_SMALL}
      >
        <RedoSingleSelectDropdownInput
          options={operationOptions}
          optionSelected={({ item }: { item: any }) =>
            handleOperationSelect(item)
          }
          placeholder={t("Select operation...")}
          selectedItem={selectedOperation}
        />
      </LabeledInput>

      <LabeledInput
        label={t("Customer tags")}
        orientation={LabelOrientation.VERTICAL}
        position={LabelPosition.ABOVE}
        size={LabelSize.EXTRA_SMALL}
      >
        <CustomerTagsMultiSelect
          requireSelection={!!step.operation}
          setSelectedTags={setSelectedTags}
          showValidationErrors={showValidationErrors}
          tagIds={step.tagIds ?? []}
        />
      </LabeledInput>
    </Flex>
  );
});

export const validateManageCustomerTagsStep = (
  step: PartialWithRequiredType<ManageCustomerTagsStep>,
) => {
  return !!(step.operation && step.tagIds && step.tagIds.length > 0);
};

export const manageCustomerTagsStepType: FrontendStepType<
  PartialWithRequiredType<ManageCustomerTagsStep>
> = {
  valid: validateManageCustomerTagsStep,
  type: "standard",
  inspectorFactory: (props) => ({
    header: { title: t("Manage customer tags") },
    sections: [
      {
        fields: [
          { widget: "toggle", label: t("Deactivate step"), path: "disabled" },
          {
            path: "customerTags",
            label: "",
            widget: "custom",
            Element: ({ value: step, valueChange: setStep }) => (
              <CustomerTagManager
                setStep={setStep}
                showValidationErrors={props.showValidationErrors}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};
